/* eslint-disable max-lines */
// istanbul ignore file
import z from 'zod';
import {
    LandlordInternationalAddress, LandlordLocalAddress, LandlordNoAddress,
} from '../landlord';
import { ContactNumberType, TenancyOccupant } from '../tenancies';
import { AddonBundleForm, AddonService } from '../pricing';
import { PropertyKeyAvailableOnsite, PropertyUtilityConnection } from '../property';
import { ServiceCode } from '../../utils';

enum EnrolmentType {
    ENROL_ONLY = 'ENROL_ONLY',
    ENROL_WITH_LEGISLATION_UPGRADE = 'ENROL_WITH_LEGISLATION_UPGRADE',
    LEGISLATION_UPGRADE_ONLY = 'LEGISLATION_UPGRADE_ONLY',
    MINIMUM_STANDARDS_ONLY = 'MINIMUM_STANDARDS_ONLY'
}

type EnrolmentV1PayloadV1 = {
    __meta: {
        isLandlordFromIntegration: boolean;
        isTenancyFromIntegration: boolean;
        isKeyDetailsFromIntegration: boolean;
        isPropertyManagerFromIntegration: boolean;
        isLandlordDetailsRequired: boolean;
        isLandlordDetailsRequiredByPricingPackage: boolean;
        isLandlordDetailsRequiredByOwnerOccupied: boolean;
        isLandlordNameRequiredByAddressToLandlord: boolean;
        isInBetweenTenancy: boolean;
        allowHonouringSubscriptions: boolean;
        occupiedPeriods: OccupiedPeriod[];
        hasPatchedTenancy: boolean;
        services: ServiceCode[];
        isVacantAtCreateDate: boolean;
        isVacantAtCreateDateWithoutOwnerOccupied: boolean;
        nextOccupiedDate: string | null;
        isVacantWithinNextWeeks: boolean;
        nextWeeksLookAheadDays: number;
        isTransferEligible: boolean;
        requireUtilityConnectionResponse: boolean;
        integratedKeyNumber: string | null;
        integratedFirstActiveDate: string | null;
        daysSinceIntegrated: number | null;
        isNewManagementReasonRequired: boolean;
        defaultIsNewManagementValue: boolean | null;
        calculatedIBTLeaseStartDate: string | null;
        nextOccupiedDateWithoutOwnerOccupied?: string | null;
        eligibleEnrolmentTypes?: EnrolmentType[] | null;
        shouldDisplayMEES: boolean;
        minimumStandardsAvailability?: {
            atAgency: boolean;
            isBlocked: boolean;
        };
        /**
         * QLD SSA upgrade: global kill switch ∧ request agency in QLD (TIB-15610).
         * Omitted or false: legacy order and copy (safe default until PRS populates this).
         */
        isQldSsaUpgradeEnabled?: boolean;
        /**
         * Agency is enrolled in the Smart Smoke Alarm program (TIB-15613).
         * Omitted or false: show the contact-account-manager marketing screen.
         */
        isSmartSmokeAlarmEnrolled?: boolean;
        /** Account manager address for the mailto link. Null when none is on file. */
        accountManagerEmail?: string | null;
        /**
         * User chose Continue with standard legislation upgrade (TIB-15613).
         * Leaves the smart wizard order and legislation-upgrade copy.
         */
        continuedWithStandardLegislationUpgrade?: boolean;
    };

    isTransfer: boolean | null;

    integrationMatch: {
        agencyKeyId: number;
        propertyReference: string;
        softwareSource: string;
        fullAddress: string;
    } | null;

    keyDetails: {
        isKeyAvailable: boolean;
        keyNumber: string | null;
    } | null;

    keyLocationId: number | null;

    onsiteKeyDetails: {
        isKeyAvailableOnsite: PropertyKeyAvailableOnsite;
        onsiteKeyAccessInstructions: string | null;
        onsiteKeyAccessibleUntil: string | null;
    } | null;

    propertyManager: {
        propertyManagerId: number;
        name: string;
    } | null;

    isLargeLadderRequired: boolean;

    utilityConnection: PropertyUtilityConnection | null;

    selectedEnrolmentType?: EnrolmentType;

    pricingDetails: {
        pricingPackageId: number;
        productId?: number;
        addonBundles?: AddonBundleForm[];
        addonServices: AddonService[];
        pricingPackageTypeIds?: number[];
        oneOffServiceCodes?: string[];
    } | null;

    services: {
        [ServiceCode.SMOKE]: {
            suffix: ServiceCode.SMOKE;
            lastServiceDate: string | null;
            integratedLastServiceDate: string | null;
            lastKnownServiceDate: string | null;
            anticipatedDueDate: string | null;
            isDeleted: boolean;
            priorityLevel: PriorityLevel | null;
            isEligibleForNewManagement: boolean;
            isOneOffService: boolean;
        };
        [ServiceCode.GAS]: {
            suffix: ServiceCode.GAS;
            lastServiceDate: string | null;
            integratedLastServiceDate: string | null;
            lastKnownServiceDate: string | null;
            anticipatedDueDate: string | null;
            isDeleted: boolean;
            priorityLevel: PriorityLevel | null;
            isEligibleForNewManagement: boolean;
            isOneOffService: boolean;
        };
        [ServiceCode.ELECTRICAL]: {
            suffix: ServiceCode.ELECTRICAL;
            lastServiceDate: string | null;
            integratedLastServiceDate: string | null;
            lastKnownServiceDate: string | null;
            anticipatedDueDate: string | null;
            isDeleted: boolean;
            priorityLevel: PriorityLevel | null;
            isEligibleForNewManagement: boolean;
            isOneOffService: boolean;
        };
    };

    landlordDetails: {
        wantsToProvidePropertyOwner: boolean;
        landlordId: number | null;
        newLandlord: {
            originalLandlordId: number | null;
            name: string;
            email: string | null;
            salutation: string | null;
            address: LandlordLocalAddress | LandlordInternationalAddress | LandlordNoAddress;
            contactNumbers: {
                number: string;
                type: ContactNumberType;
            }[];
        } | null;
    } | null;

    /**
     * @deprecated
     */
    tenancyMeta: {
        /**
         * @deprecated
         */
        isVacantAtCreateDate: boolean;
        /**
         * @deprecated
         */
        occupiedByUpdatesRequiredThreshold: boolean;
    } | null;

    ownerOccupied: {
        isAtPropertyTemporarily: false;
        moveInDate: string | null;
        moveOutDate: null;
    } | {
        isAtPropertyTemporarily: true;
        moveInDate: string | null;
        moveOutDate: string;
    } | null;

    tenancy: {
        startDate: string | null;
        nextRenewalDate: string | null;
        endDate: string | null;
        vacatingDate: string | null;
        occupants: TenancyOccupant[];
    } | null;

    nextTenancy: {
        startDate: string;
        occupants: TenancyOccupant[];
    } | null;

    hasIntegratedTenancyDataConflict: boolean | null;

    honouringSubscriptionDate: string | null;

    accessInstructions: {
        isDogOnSite: boolean;
        isPetOnSite: boolean;
        isLanguageBarrier: boolean;
        isHearingImpaired: boolean;
        accessNotes: string | null;
        bookingNotes: string | null;
    };

    hasUnknownNextTenancy: boolean;

    inbetweenTenancyPriority:
    {
        isRequired: true;
        leaseStartDate: string;
    } | {
        isRequired: false;
        leaseStartDate: null;
    } | null;

    isNewManagement: boolean | null;
    newManagementReason: string | null;

    legislationUpgradeDetails: LegislationUpgradeDetail | null;

    includeMinimumStandardsInPerson?: boolean | null;

    minimumStandardsOptions?: {
        includeCordedBlinds: boolean;
        includeMEESChecks: boolean;
    };
};

type PropertyDetailsPayload =
    Partial<Pick<EnrolmentV1PayloadV1, 'keyDetails' | 'keyLocationId' | 'utilityConnection' | 'isNewManagement' | 'newManagementReason'> & {
        propertyManager: {
            propertyManagerId: number;
        } | null;
    }>;

type LandlordDetailsPayload = Pick<EnrolmentV1PayloadV1, 'landlordDetails'>;

type PricingDetailsPayload = Required<Pick<EnrolmentV1PayloadV1, 'pricingDetails'>>;

type PriorityLevel = {
    isPublic: boolean;
    internalName: string;
    externalName: string;
    code: string;
    notes: string;
    sortOrder: number;
    slaHours: number | null;
};

type ServiceDatesPayload = {
    services?: {
        [ServiceCode.SMOKE]?: {
            lastServiceDate: string | null;
        };
        [ServiceCode.GAS]?: {
            lastServiceDate: string | null;
        };
        [ServiceCode.ELECTRICAL]?: {
            lastServiceDate: string | null;
        };
    };
    honouringSubscriptionDate?: string | null;
};

type OccupiedPeriod = {
    start: string | null;
    end: string | null;
};

type AccessInstructionsPayload = Pick<EnrolmentV1PayloadV1, 'accessInstructions' | 'onsiteKeyDetails' | 'isLargeLadderRequired'>;

type OwnerOccupiedPayload = Pick<EnrolmentV1PayloadV1, 'ownerOccupied' | 'hasIntegratedTenancyDataConflict'>;

enum LegislationUpgradeTypes {
    FOR_OWNER_OCCUPIED_PROPERTY = '1',
    FOR_PROPERTY_BEING_SOLD = '2',
    FOR_PROPERTY_BEING_LEASED = '3'
}

enum LegislationUpgradeSource {
    PublicWebsite = 'PUBLIC_WEBSITE',
    AgencyRequest = 'AGENCY_REQUEST',
    PhoneCall = 'PHONE_CALL',
    Email = 'EMAIL'
}

const LegislationUpdateDetailSchema = z.object({
    appliancePrice: z.number(),
    bedrooms: z.number(),
    depositAmount: z.number(),
    depositPercentage: z.number(),
    propertyTypeId: z.enum([
        LegislationUpgradeTypes.FOR_OWNER_OCCUPIED_PROPERTY,
        LegislationUpgradeTypes.FOR_PROPERTY_BEING_LEASED,
        LegislationUpgradeTypes.FOR_PROPERTY_BEING_SOLD,
    ]),
    isStudioProperty: z.boolean().default(false),
    storeys: z.number(),
    totalAmount: z.number(),
    upgradeOfferId: z.string(),
    overrideAmount: z.number().optional(),
    smokeAlarmType: z.enum(['interconnected', 'ssa']),
    useCustomQuote: z.boolean(),
    includeRemoteControl: z.boolean().default(false),
    remoteControlPrice: z.number(),
    alarmCounts: z.number().nullable().default(null),
    upgradeSource: z.nativeEnum(LegislationUpgradeSource),
});
type LegislationUpgradeDetail = z.infer<typeof LegislationUpdateDetailSchema>;

export {
    EnrolmentType,
    EnrolmentV1PayloadV1,
    PropertyDetailsPayload,
    PricingDetailsPayload,
    ServiceDatesPayload,
    LandlordDetailsPayload,
    AccessInstructionsPayload,
    OwnerOccupiedPayload,
    LegislationUpgradeTypes,
    LegislationUpgradeSource,
};
